import React from 'react';
import { X, Download, ExternalLink } from 'lucide-react';
import { Button } from './button';

/**
 * PDF Viewer Modal Component
 * Displays PDFs with proper scrolling and controls
 */
export const PDFViewer = ({ isOpen, onClose, pdfUrl, title }) => {
  if (!isOpen) return null;

  const handleClose = () => {
    if (pdfUrl && pdfUrl.startsWith('blob:')) {
      window.URL.revokeObjectURL(pdfUrl);
    }
    onClose();
  };

  const openInNewTab = () => {
    window.open(pdfUrl, '_blank');
  };

  return (
    <div 
      className="fixed inset-0 z-[100] bg-black/80 flex items-center justify-center p-4"
      onClick={handleClose}
      data-testid="pdf-viewer-overlay"
    >
      <div 
        className="bg-white rounded-lg w-full max-w-5xl h-[90vh] flex flex-col shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gray-100 rounded-t-lg shrink-0">
          <h3 className="text-lg font-semibold text-gray-800 truncate">{title || 'PDF Preview'}</h3>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={openInNewTab}
              className="text-gray-600 hover:text-gray-800"
              title="Open in new tab"
            >
              <ExternalLink className="h-4 w-4 mr-1" />
              Open
            </Button>
            <a 
              href={pdfUrl} 
              download={`${title || 'document'}.pdf`}
              className="inline-flex items-center px-3 py-1.5 text-sm bg-primary text-white rounded hover:bg-primary/90"
            >
              <Download className="h-4 w-4 mr-1" />
              Download
            </a>
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={handleClose}
              className="text-gray-600 hover:text-gray-800 hover:bg-gray-200"
              data-testid="pdf-viewer-close"
            >
              <X className="h-5 w-5" />
            </Button>
          </div>
        </div>
        
        {/* PDF Content - with proper scrolling */}
        <div className="flex-1 bg-gray-200 overflow-auto">
          <iframe 
            src={pdfUrl}
            title={title || 'PDF Preview'}
            className="w-full h-full min-h-[800px]"
            style={{ border: 'none' }}
          />
        </div>

        {/* Footer with close button */}
        <div className="p-3 border-t border-gray-200 bg-gray-100 rounded-b-lg shrink-0 flex justify-end">
          <Button
            onClick={handleClose}
            variant="outline"
            data-testid="pdf-viewer-close-btn"
          >
            Close Preview
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PDFViewer;
